import { useState } from "react";
import { createPortal } from "react-dom";
import axios from "axios";
import { CaretRight, Plus } from "@phosphor-icons/react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Tally-style group picker: typing in the input filters a separate
// "List of Groups" panel fixed at the right edge of the screen.
// ArrowUp/Down + Enter select, Esc closes, Ctrl+A creates a new group.
export const GroupPicker = ({ groups, value, onChange, onGroupCreated, testid = "group-picker" }) => {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [hi, setHi] = useState(0);
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const selected = groups.find((g) => g.id === value);
  const filtered = q
    ? groups.filter((g) => g.name.toLowerCase().includes(q.toLowerCase()))
    : groups;

  const close = () => { setOpen(false); setQ(""); setCreating(false); setErr(""); };

  const pick = (g) => {
    onChange(g);
    close();
  };

  const createGroup = async () => {
    const name = newName.trim();
    if (!name || busy) return;
    setBusy(true);
    setErr("");
    try {
      const r = await axios.post(`${API}/account-groups`, { name });
      if (onGroupCreated) onGroupCreated(r.data);
      pick(r.data);
    } catch (e2) {
      setErr(e2.response?.data?.detail || "Failed to create group");
    }
    setBusy(false);
  };

  const onKey = (e) => {
    if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "a") {
      e.preventDefault();
      setOpen(true);
      setCreating(true);
      setNewName(q);
      setErr("");
      return;
    }
    if (e.key === "ArrowDown") {
      e.preventDefault();
      if (!open) setOpen(true);
      else setHi((h) => Math.min(h + 1, filtered.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHi((h) => Math.max(h - 1, 0));
    } else if (e.key === "Enter") {
      if (open && !creating) {
        e.preventDefault();
        if (filtered[hi]) pick(filtered[hi]);
      }
    } else if (e.key === "Escape") {
      close();
    }
  };

  return (
    <div className="relative">
      <input
        data-testid={`${testid}-input`}
        value={open ? q : (selected?.name || "")}
        placeholder="Type to search groups… (Ctrl+A: new group)"
        onFocus={() => { setOpen(true); setQ(""); setHi(0); }}
        onChange={(e) => { setQ(e.target.value); setHi(0); if (!open) setOpen(true); }}
        onKeyDown={onKey}
        className="w-full px-3 py-2 border border-slate-200 rounded-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 text-sm"
        autoComplete="off"
      />
      {creating && (
        <div className="absolute left-0 right-0 top-full mt-1 z-40 p-3 bg-amber-50 border-2 border-amber-300 rounded-sm shadow-lg" data-testid={`${testid}-create-form`}>
          <p className="text-[11px] font-black uppercase tracking-wider text-amber-800 mb-1.5">Create New Group</p>
          <input
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") { e.preventDefault(); createGroup(); }
              else if (e.key === "Escape") { e.stopPropagation(); setCreating(false); setErr(""); }
            }}
            placeholder="New group name"
            autoFocus
            data-testid={`${testid}-create-input`}
            className="w-full px-2.5 py-1.5 border border-amber-300 rounded-sm text-sm mb-2"
            data-enter-nav-skip="true"
          />
          {err && <p className="text-[11px] font-bold text-red-600 mb-2" data-testid={`${testid}-create-error`}>{err}</p>}
          <div className="flex gap-2">
            <button type="button" onClick={createGroup} disabled={busy || !newName.trim()}
              data-testid={`${testid}-create-save`}
              className="flex-1 px-3 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-sm text-xs font-bold disabled:opacity-50 flex items-center justify-center gap-1">
              <Plus size={12} weight="bold" /> {busy ? "Saving…" : "Save Group"}
            </button>
            <button type="button" onClick={() => { setCreating(false); setErr(""); }}
              data-testid={`${testid}-create-cancel`}
              className="px-3 py-1.5 border border-slate-300 rounded-sm text-xs font-bold text-slate-600 hover:bg-slate-100">
              Cancel
            </button>
          </div>
        </div>
      )}
      {open && createPortal(
        <div
          className="fixed top-1/2 -translate-y-1/2 right-6 h-[80vh] w-72 bg-white border-2 border-emerald-400 rounded-sm shadow-2xl z-[120] flex flex-col pointer-events-auto"
          data-group-picker-panel="true"
          data-testid={`${testid}-panel`}
        >
          <div className="px-4 py-2.5 bg-emerald-900 text-white text-xs font-black uppercase tracking-widest text-center flex-shrink-0">
            List of Groups
          </div>
          {q && !creating && (
            <div className="px-4 py-1.5 bg-emerald-50 border-b border-emerald-100 text-[11px] font-bold text-emerald-800 flex-shrink-0" data-testid={`${testid}-filter-hint`}>
              "{q}" — {filtered.length} match{filtered.length === 1 ? "" : "es"}
            </div>
          )}
          <div className="flex-1 overflow-y-auto bg-emerald-50/40">
            {filtered.length === 0 && !creating && (
              <p className="text-xs text-slate-400 text-center py-6">No matching group — press Ctrl+A to create one</p>
            )}
            {filtered.map((g, i) => (
              <button
                key={g.id}
                type="button"
                ref={i === hi ? (el) => el && el.scrollIntoView({ block: "nearest" }) : undefined}
                onMouseDown={(e) => { e.preventDefault(); pick(g); }}
                onMouseEnter={() => setHi(i)}
                data-testid={`${testid}-option-${g.id}`}
                className={`w-full text-left px-4 py-1.5 text-sm flex items-center gap-1.5 ${
                  i === hi
                    ? "bg-emerald-600 text-white font-bold"
                    : g.id === value
                      ? "bg-emerald-100 text-emerald-900 font-semibold"
                      : "text-slate-800 hover:bg-emerald-100"
                }`}
              >
                {i === hi && <CaretRight size={11} weight="bold" />}
                {g.name}
              </button>
            ))}
          </div>
          <div className="px-4 py-1.5 bg-slate-50 border-t border-slate-200 text-[10px] font-semibold text-slate-500 text-center flex-shrink-0">
            ↑↓ Navigate · Enter Select · Ctrl+A New Group
          </div>
          <button
            type="button"
            onMouseDown={(e) => { e.preventDefault(); close(); }}
            data-testid={`${testid}-close`}
            className="px-4 py-2 text-xs font-bold text-slate-500 hover:text-slate-900 hover:bg-slate-100 border-t border-slate-200 flex-shrink-0"
          >
            Close (Esc)
          </button>
        </div>,
        document.body
      )}
    </div>
  );
};
